import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Star, ShoppingCart, Check, ShieldCheck, Truck, Minus, Plus, ChevronRight,
} from "lucide-react";
import { fetchProduct } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PsuSizingGuide from "@/components/PsuSizingGuide";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [psu, setPsu] = useState(null);
  const [fans, setFans] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    fetchProduct(id)
      .then((p) => {
        setProduct(p);
        setVariant(p.variants[0]);
        setPsu((p.psu_options || [])[0] || null);
        setFans((p.fan_options || [])[0] || null);
        setActiveImg(0);
        setQty(1);
      })
      .catch(() => navigate("/shop"));
  }, [id, navigate]);

  if (!product) {
    return <div data-testid="product-loading" className="mx-auto max-w-7xl px-5 py-32 text-center text-[#A1A1AA]">Loading…</div>;
  }

  const selectVariant = (v) => {
    setVariant(v);
    const idx = (product.image_map || {})[v.id];
    if (idx !== undefined) setActiveImg(idx);
  };

  const handleAdd = () => {
    const label = [variant.label, psu && `${psu.label} PSU`, fans && fans.label].filter(Boolean).join(" · ");
    addItem({
      product_id: product.id,
      variant_id: variant.id,
      psu: psu ? psu.id : null,
      fans: fans ? fans.id : null,
      quantity: qty,
      name: product.name,
      variant_label: label,
      price: variant.price,
      image: product.images[(product.image_map || {})[variant.id] ?? 0],
    });
    toast.success(`${product.name} added to cart`, { description: `${label} × ${qty}` });
  };

  return (
    <div data-testid="product-detail-page" className="mx-auto max-w-7xl px-5 py-12 lg:px-8 lg:py-16">
      <nav className="flex items-center gap-2 text-xs text-[#A1A1AA]">
        <Link to="/shop" className="hover:text-primary">Shop</Link>
        <ChevronRight size={12} />
        <span className="text-[#FAFAFA]">{product.name}</span>
      </nav>

      <div className="mt-8 grid gap-12 lg:grid-cols-2">
        {/* Gallery */}
        <div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="overflow-hidden rounded-sm border border-[#27272A] bg-[#18181B]">
            <img src={product.images[activeImg]} alt={product.name} className="aspect-square w-full object-cover" data-testid="product-main-image" />
          </motion.div>
          <div className="mt-4 grid grid-cols-4 gap-3">
            {product.images.map((img, i) => (
              <button
                key={i}
                data-testid={`thumb-${i}`}
                onClick={() => setActiveImg(i)}
                className={`overflow-hidden rounded-sm border bg-[#18181B] transition-colors ${
                  activeImg === i ? "border-primary" : "border-[#27272A] hover:border-[#3f3f46]"
                }`}
              >
                <img src={img} alt={`${product.name} ${i + 1}`} className="aspect-square w-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div>
          {product.badge && (
            <span className="inline-block rounded-sm bg-primary px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#09090B]">
              {product.badge}
            </span>
          )}
          <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-[#FAFAFA] sm:text-4xl">
            {product.name}
          </h1>
          <div className="mt-3 flex items-center gap-3 text-sm text-[#A1A1AA]">
            <span className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-primary text-primary" />)}
              {product.rating}
            </span>
            <span>·</span>
            <span>{product.reviews} reviews</span>
          </div>
          <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-[#A1A1AA]">{product.description}</p>

          <div className="mt-6 font-mono text-3xl font-semibold text-[#FAFAFA]" data-testid="product-price">
            €{variant.price.toFixed(2)}
          </div>

          {/* Configuration */}
          {product.variants.length > 1 && (
          <div className="mt-7">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Select configuration</span>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {product.variants.map((v) => (
                <button
                  key={v.id}
                  data-testid={`variant-${v.id}`}
                  onClick={() => selectVariant(v)}
                  className={`flex items-center justify-between rounded-sm border px-4 py-3 text-left text-sm transition-colors ${
                    variant.id === v.id
                      ? "border-primary bg-[#F97316]/10 text-[#FAFAFA]"
                      : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"
                  }`}
                >
                  <span>{v.label}</span>
                  {variant.id === v.id && <Check size={16} className="text-primary" />}
                </button>
              ))}
            </div>
          </div>
          )}

          {/* PSU distance */}
          {product.psu_options && (
            <div className="mt-6">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Select PSU length</span>
              <div className="mt-3 grid grid-cols-3 gap-3">
                {product.psu_options.map((o) => (
                  <button
                    key={o.id}
                    data-testid={`psu-${o.id}`}
                    onClick={() => setPsu(o)}
                    className={`rounded-sm border px-4 py-3 text-center text-sm transition-colors ${
                      psu && psu.id === o.id
                        ? "border-primary bg-[#F97316]/10 text-[#FAFAFA]"
                        : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <PsuSizingGuide selected={psu ? psu.id : null} />
              </div>
            </div>
          )}

          {/* Fan option */}
          {product.fan_options && (
            <div className="mt-6">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Stock fans</span>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {product.fan_options.map((o) => (
                  <button
                    key={o.id}
                    data-testid={`fans-${o.id}`}
                    onClick={() => setFans(o)}
                    className={`flex items-center justify-between rounded-sm border px-4 py-3 text-left text-sm transition-colors ${
                      fans && fans.id === o.id
                        ? "border-primary bg-[#F97316]/10 text-[#FAFAFA]"
                        : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"
                    }`}
                  >
                    <span>{o.label}</span>
                    {fans && fans.id === o.id && <Check size={16} className="text-primary" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Qty + Add */}
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <div className="flex items-center rounded-sm border border-[#27272A]">
              <button data-testid="qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-12 w-12 items-center justify-center text-[#FAFAFA] hover:text-primary">
                <Minus size={16} />
              </button>
              <span data-testid="qty-value" className="w-10 text-center font-mono text-[#FAFAFA]">{qty}</span>
              <button data-testid="qty-increase" onClick={() => setQty((q) => Math.min(99, q + 1))} className="flex h-12 w-12 items-center justify-center text-[#FAFAFA] hover:text-primary">
                <Plus size={16} />
              </button>
            </div>
            <button
              data-testid="add-to-cart-button"
              onClick={handleAdd}
              className="group flex flex-1 items-center justify-center gap-2 rounded-sm bg-primary px-7 py-3.5 text-sm font-semibold text-[#09090B] transition-all hover:bg-[#EA580C] hover:shadow-[0_0_20px_rgba(249,115,22,0.4)]"
            >
              <ShoppingCart size={17} /> Add to cart
            </button>
          </div>

          <div className="mt-6 flex flex-wrap gap-5 text-xs text-[#A1A1AA]">
            <span className="flex items-center gap-2"><ShieldCheck size={15} className="text-primary" /> Secure Stripe checkout</span>
            <span className="flex items-center gap-2"><Truck size={15} className="text-primary" /> Ships within 48h</span>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="specs" className="mt-10">
            <TabsList className="w-full justify-start rounded-sm border border-[#27272A] bg-[#0E0E11]">
              <TabsTrigger value="specs" data-testid="tab-specs">Specifications</TabsTrigger>
              <TabsTrigger value="features" data-testid="tab-features">Features</TabsTrigger>
              <TabsTrigger value="shipping" data-testid="tab-shipping">Shipping</TabsTrigger>
            </TabsList>
            <TabsContent value="specs" className="mt-5">
              <div className="overflow-hidden rounded-sm border border-[#27272A]">
                {Object.entries(product.specs).map(([k, v], i) => (
                  <div key={k} className={`grid grid-cols-1 gap-1 px-4 py-3 text-sm sm:grid-cols-[160px_1fr] sm:gap-4 ${i % 2 ? "bg-[#0E0E11]" : "bg-[#09090B]"}`}>
                    <span className="text-[#A1A1AA]">{k}</span>
                    <span className="font-mono leading-relaxed text-[#FAFAFA] sm:text-right">{v}</span>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="features" className="mt-5">
              <ul className="space-y-3">
                {product.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-[#A1A1AA]">
                    <Check size={16} className="mt-0.5 shrink-0 text-primary" /> {f}
                  </li>
                ))}
              </ul>
            </TabsContent>
            <TabsContent value="shipping" className="mt-5 text-sm leading-relaxed text-[#A1A1AA]">
              Orders ship within 48 hours via tracked courier. Worldwide delivery available.
              Every order is protected by our 12-month warranty and 30-day return policy.
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
